<template>
  <div class="flex flex-col">
    <div class="flex justify-between items-center">
      <dropdown-button name="gptVersion"
      :options="gptVersionOptions"
      :primary-button-variant="isDisabled ? 'dropdown-header-disabled' : 'dropdown-header'"
      :close-on-select="true"
      :value="resolvedDeploymentId"
      :key="actionId"
      primary-button-classes="w-72"
      arrow-color="black-a20"
      @select-button="updateGptVersion"
      :is-disabled="isDisabled"
      />

      <span v-if="isDisabled && !hideSections" class="text-gray-dark text-xs">(read-only)</span>
    </div>

    <BaseCallout v-if="isEmailBot && !isDisabled"
      class="mt-4"
      text='If Chat GPT reviews an email query and suggests handing it over to an operator, then the bot will NOT send an email response.<br/><br/>
        Use condition case "Chat GPT Call Operator" to send an email response and/or call an operator.'
    />

    <div class="grid grid-cols-2 gap-2 mt-4">
      <div class="col-span-1">
        <base-label :id="KNOWLEDGE_TYPE.id"
                    :label="KNOWLEDGE_TYPE.label"
                    :tooltip="KNOWLEDGE_TYPE.tooltip"
        />
        <base-select :variant="isDisabled ? 'baseSelectDisabled' : 'baseSelect'"
                     :value="selectedActionType"
                     :options="actionTypeOptions"
                     class="w-full border-gray mt-1 h-10"
                     :is-disabled="isDisabled"
                     @input="updateActionType"
        />
      </div>

      <with-error-message v-if="isActionTypeChatGptCustom"
                          :show-error="isIndexIdError"
                          class="col-span-1 relative"
      >
        <base-label :id="KNOWLEDGE_BASE.id"
                    :label="KNOWLEDGE_BASE.label"
                    :tooltip="KNOWLEDGE_BASE.tooltip"
        />
        <div v-if="!isLoading && knowledgeBaseOptions.length === 0" class="h-10 mt-1 flex items-center">
          <span>None exist.</span>
          <router-link :to="{
                         name: ROUTE_NAMES.knowledgeBases,
                         params: { knowledgeBaseId: ITEM_IDS.NEW }
                       }"
                       class="ml-1 underline hover:text-black">
            Create?
          </router-link>
        </div>
        <base-select v-else
                     :variant="knowledgeBaseSelectVariant"
                     :value="selectedKnowledgeBaseKey"
                     :options="knowledgeBaseOptions"
                     :value-attribute="'selectionKey'"
                     :text-attribute="'name'"
                     :is-disabled="isDisabled"
                     class="w-full border-gray mt-1 h-10"
                     @input="updateKnowledgeBaseSelection"
        />
        <router-link v-if="selectedKnowledgeBaseRoute"
                     :to="selectedKnowledgeBaseRoute"
                     class="mt-1 flex items-center gap-1 text-xs"
        >
          <span class="hover:underline">View Knowledge Base</span>
          <vs-icon icon="call_made" size="1.2rem"/>
        </router-link>
        <div v-if="!hideSections && (showKnowledgeBaseNotReadyWarning || selectedIndexGroupMissingMarkets)"
             class="absolute left-0 mt-0.5 flex flex-col gap-0.5"
        >
          <vs-tooltip v-if="showKnowledgeBaseNotReadyWarning" position="top">
            <template #content>
              <div v-html="'Knowledge base creation is in progress. When ready, the bot will be able to use it.'"/>
            </template>
            <div class="text-xs text-gray-dark">
              ⚠️ Knowledge base is not ready.
            </div>
          </vs-tooltip>
          <vs-tooltip v-if="selectedIndexGroupMissingMarkets" position="top">
            <template #content>
              <div v-html="selectedIndexGroupMissingMarkets.tooltip"/>
            </template>
            <router-link v-if="selectedIndexGroupMissingMarkets.route"
                         :to="selectedIndexGroupMissingMarkets.route"
                         class="text-xs text-gray-dark underline hover:text-black"
            >
              ⚠️ Add missing markets
            </router-link>
          </vs-tooltip>
        </div>
      </with-error-message>
    </div>

    <form @input="updateAction" class="mt-4 flex flex-col gap-4">
      <chat-GPT-editor-form-row-full v-if="!hideSections"
                                     :item="PROMPT"
                                     :is-item-error="isPromptError"
                                     :row-count="20"
                                     :token-count="promptTokenCount"
                                     :is-disabled="isDisabled"
                                     :action="action"
      />

      <chat-GPT-editor-form-row-full v-if="!hideSections"
                                     :item="USER_PROMPT"
                                     :is-item-error="isUserPromptError && !!action.userPrompt"
                                     :row-count="10"
                                     :token-count="userPromptTokenCount"
                                     :placeholder="isDisabled ? '' : 'optional'"
                                     :is-disabled="isDisabled"
                                     :action="action"
      />

      <chat-GPT-editor-form-row-full v-if="!hideSections && isActionTypeChatGptCustom"
                                     :item="SEARCH_METADATA"
                                     :is-item-error="isSearchMetadataError && !!action.searchMetadata && action.searchMetadata.length <= searchMetadata.lengthLimit"
                                     :row-count="4"
                                     :token-count="searchMetadataTokenCount"
                                     :placeholder="isDisabled ? '' : 'optional'"
                                     :is-disabled="isDisabled"
                                     :action="action"
      />

      <chat-GPT-editor-form-row-split v-if="!hideSections && showUnreleased"
                                      :first-item="MAX_TOKENS"
                                      :is-first-item-error="isMaxTokensError"
                                      :is-first-item-admin="true"
                                      :is-disabled="isDisabled"
                                      :action="action"
      />

      <div class="grid grid-cols-2 gap-2">
        <div class="col-span-1">
          <base-label :id="TEMPERATURE.id"
                      :label="TEMPERATURE.label"
                      :tooltip="TEMPERATURE.tooltip"
          />
          <base-select :variant="isDisabled ? 'baseSelectDisabled' : 'baseSelect'"
                       :value="selectedCreativityLevel"
                       :options="creativityLevelOptions"
                       class="mt-1 h-10"
                       :is-disabled="isDisabled"
                       @input="updateActionCreativityLevel"
          />
        </div>

        <div v-if="!hideSections" class="col-span-1">
          <base-label id="tokenCount"
                      label="Token count"
                      tooltip="System prompt tokens<br/>+ User prompt tokens<br/>+ Search metadata tokens<br/><b>= Token count</b>"
          />
          <span class="flex h-10 p-3">
            {{ totalTokenCount }} tokens
          </span>
        </div>
      </div>

      <chat-GPT-editor-form-row-split :second-item="TOP_P"
                                      :is-second-item-error="isTopPError"
                                      :is-second-item-admin="true"
                                      :is-disabled="isDisabled"
                                      :action="action"
      />

      <chat-GPT-editor-form-row-split v-if="showUnreleased"
                                      :first-item="FREQUENCY_PENALTY"
                                      :is-first-item-error="isFrequencyPenaltyError"
                                      :is-first-item-admin="true"
                                      :second-item="PRESENCE_PENALTY"
                                      :is-second-item-error="isPresencePenaltyError"
                                      :is-second-item-admin="true"
                                      :is-disabled="isDisabled"
                                      :action="action"
      />

      <chat-GPT-editor-form-row-split v-if="showUnreleased && isActionTypeChatGptCustom"
                                      :first-item="TOP_N_DOCUMENTS"
                                      :is-first-item-error="isTopNDocumentsError"
                                      :is-first-item-admin="true"
                                      :second-item="STRICTNESS"
                                      :is-second-item-error="isStrictnessError"
                                      :is-second-item-admin="true"
                                      :is-disabled="isDisabled"
                                      :action="action"
      />

      <chat-GPT-editor-tools :action="action"
                             :action-id="actionId"
                             :is-disabled="isDisabled"
      />

      <template>
        <vx-tooltip text="Include past messages in each new GPT request. This helps give the model context for new user queries."
                    position="top"
                    class="flex items-center"
        >
          <checkbox id="includeMessageHistoryCheckbox"
                    :is-checked="includeMessageHistory"
                    :is-disabled="isDisabled"
                    @update:isChecked="handleMessageHistoryCheckbox"
          />
          <label for="includeMessageHistoryCheckbox"
                 class="ml-2 text-gray-dark cursor-pointer leading-tight"
                 :class="isDisabled ? 'cursor-not-allowed' : 'cursor-default'"
          >
            Include message history
          </label>
        </vx-tooltip>

        <vx-tooltip text="Lets the AI collect exact values it needs for a tool (email, phone, order number, …) through validated input fields instead of free text. Webchat only; the answer should end with “Continue or Find answer” so the reply returns to this action."
                    position="top"
                    class="flex items-center"
        >
          <checkbox id="allowInputFieldsCheckbox"
                    :is-checked="allowInputFields"
                    :is-disabled="isDisabled"
                    @update:isChecked="handleAllowInputFieldsCheckbox"
          />
          <label for="allowInputFieldsCheckbox"
                 class="ml-2 text-gray-dark cursor-pointer leading-tight"
                 :class="isDisabled ? 'cursor-not-allowed' : 'cursor-default'"
          >
            Allow LLM to use structured input fields
          </label>
        </vx-tooltip>
      </template>

    </form>

    <chat-GPT-editor-product-recommendation v-if="!hideSections && isActionTypeChatGptCustom"
                                            class="mt-4"
                                            :action="action"
                                            :action-id="actionId"
                                            :is-disabled="isDisabled"
    />

    <base-button v-if="!isDisabled" variant="bordered-error" class="w-full mt-8" @click="removeChatGPT">
      Remove Chat GPT
    </base-button>
  </div>
</template>

<script>
import answerController from '@/controllers/answer.controller'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import previewModule from '@/store/modules/preview.module'
import stateTreeUtils from '@/utils/state-tree.utils'
import templateController from '@/controllers/template.controller'
import templateModule from '@/store/modules/template.module'
import { ACTION_TYPES, GPT_VERSIONS, INDEX_TYPES } from '@/utils/bot-json.constants'
import { ITEM_IDS } from '@/utils/editor.constants'
import { CHAT_GPT_EDITOR_PROPS } from '@/constants/chat-gpt.constants'
import { ROUTE_NAMES } from '@/router'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'
import Checkbox from '@/components/preview/Checkbox'
import DropdownButton from '@/components/common/DropdownButton'
import ChatGPTEditorFormRowFull from '@/components/editor/ChatGPTEditorFormRowFull'
import ChatGPTEditorFormRowSplit from '@/components/editor/ChatGPTEditorFormRowSplit'
import ChatGPTEditorTools from '@/components/editor/ChatGPTEditorTools'
import ChatGPTEditorProductRecommendation from '@/components/editor/ChatGPTEditorProductRecommendation'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import $script from 'scriptjs'
import isEmpty from 'ramda/src/isEmpty'
import indexesService from '@/services/amio/indexes.service'
import indexGroupService from '@/services/amio/index-group.service'
import marketService from '@/services/amio/market.service'
import botModule from '@/store/modules/bot.module'
import marketModule from '@/store/modules/market.module'
import {
  buildMissingKnowledgeMarketsTooltip,
  getFirstIndexIdForIndexGroup,
  getMissingKnowledgeMarketsForIndexGroup
} from '@/utils/index.utils'
import { getMarketsForCurrentBot } from '@/utils/market.utils'

const CHAT_GPT_CUSTOM_GENERAL = 'chatgpt-custom-general' // only used in this component, not a valid action type in botJson

const CHATGPT_ACTION_TYPES_LABEL_MAP = {
  [ACTION_TYPES.CHAT_GPT_GENERAL]: 'General',
  [ACTION_TYPES.CHAT_GPT_CUSTOM]: 'Custom',
  [CHAT_GPT_CUSTOM_GENERAL]: 'Custom + General'
}

const LABEL_CHATGPT_ACTION_TYPES_MAP = {
  'General': ACTION_TYPES.CHAT_GPT_GENERAL,
  'Custom': ACTION_TYPES.CHAT_GPT_CUSTOM,
  'Custom + General': CHAT_GPT_CUSTOM_GENERAL
}

const CREATIVITY_LABEL_TEMPERATURE_MAP = {
  'Strict': 0,
  'Balanced': 0.7,
  'Creative': 1.5
}

const {
  KNOWLEDGE_TYPE,
  KNOWLEDGE_BASE,
  PROMPT,
  USER_PROMPT,
  SEARCH_METADATA,
  MAX_TOKENS,
  TEMPERATURE,
  TOP_P,
  FREQUENCY_PENALTY,
  PRESENCE_PENALTY,
  TOP_N_DOCUMENTS,
  STRICTNESS
} = CHAT_GPT_EDITOR_PROPS

export default {
  name: 'ChatGPTEditor',

  components: {
    Checkbox,
    DropdownButton,
    ChatGPTEditorFormRowFull,
    ChatGPTEditorFormRowSplit,
    ChatGPTEditorTools,
    ChatGPTEditorProductRecommendation,
    WithErrorMessage
  },

  props: {
    isDisabled: {
      type: Boolean,
      default: false
    },
    hideSections: {
      type: Boolean,
      default: false
    },
    cachedAction: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      ROUTE_NAMES,
      ITEM_IDS,
      isLoading: false,
      indexes: [],
      indexGroups: [],
      toggleRefreshKey: 0,
      tokenizer: null,
      KNOWLEDGE_TYPE,
      KNOWLEDGE_BASE,
      PROMPT,
      USER_PROMPT,
      SEARCH_METADATA,
      MAX_TOKENS,
      TEMPERATURE,
      TOP_P,
      FREQUENCY_PENALTY,
      PRESENCE_PENALTY,
      TOP_N_DOCUMENTS,
      STRICTNESS
    }
  },

  watch: {
    actionId: {
      immediate: true,
      handler: function (actionId) {
        if (this.isDisabled) return

        if (!Object.values(CREATIVITY_LABEL_TEMPERATURE_MAP).includes(this.action.temperature)) {
          // default to creativity level "Strict"
          conversationModule.updateChatGPTAction(this.actionId, { ...this.action, temperature: 0 })
        }
        if (!this.showUnreleased && this.action.maxTokens !== 4096) {
          conversationModule.updateChatGPTAction(this.actionId, { ...this.action, maxTokens: 4096 })
        }
        if (this.action.messageHistoryCount) {
          conversationModule.updateChatGPTAction(this.actionId, { ...this.action, messageHistoryCount: 30 })
        }

        const gptParams = ['userPrompt', 'topP', 'frequencyPenalty', 'presencePenalty', 'deploymentId', 'allowInputFields']
        if (this.action.type === ACTION_TYPES.CHAT_GPT_CUSTOM) {
          gptParams.push('searchMetadata', 'topNDocuments', 'strictness', 'dataRetrievalType', 'indexType')
        }
        const existingActionParams = Object.keys(this.action)
        const missingParams = gptParams.filter(key => !existingActionParams.includes(key))
        if (isEmpty(missingParams)) return

        const defaultParamMap = {
          userPrompt: '',
          searchMetadata: '',
          topP: 0,
          frequencyPenalty: 0,
          presencePenalty: 0,
          topNDocuments: 5,
          strictness: 3,
          deploymentId: GPT_VERSIONS.FOUR_OMNI_MINI,
          allowInputFields: false,
          dataRetrievalType: 'default',
          indexType: INDEX_TYPES.SINGLE
        }
        const actionParams = {}
        for (const key of missingParams) {
          actionParams[key] = defaultParamMap[key]
        }
        conversationModule.updateChatGPTAction(this.actionId, { ...this.action, ...actionParams })
      }
    }
  },

  computed: {
    bubbleSelected: chatBubbleModule.bubbleSelected,
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,
    stateTree: chatBubbleModule.stateTree,
    template: templateModule.template,

    actionIndexId() {
      return this.action?.indexId || ''
    },

    resolvedIndexGroupId() {
      if (!this.actionIndexId) return null

      const selectedIndex = this.indexes.find(index => index.id === this.actionIndexId)
      if (selectedIndex?.index_group_id) return selectedIndex.index_group_id

      if (this.isIndexTypeGroup) return this.actionIndexId

      return null
    },

    selectedKnowledgeBaseKey() {
      if (!this.actionIndexId) return ''

      if (this.resolvedIndexGroupId) {
        return `${INDEX_TYPES.GROUP}:${this.resolvedIndexGroupId}`
      }

      return `${INDEX_TYPES.SINGLE}:${this.actionIndexId}`
    },

    selectedKnowledgeBaseRoute() {
      if (!this.actionIndexId) return null

      let knowledgeBaseId = this.actionIndexId
      if (this.resolvedIndexGroupId) {
        const botMarketId = botModule.getBot()?.market_id
        const marketIndex = this.indexes.find(index =>
          index.index_group_id === this.resolvedIndexGroupId && index.market_id === botMarketId
        )
        knowledgeBaseId = marketIndex?.id || getFirstIndexIdForIndexGroup(this.indexes, this.resolvedIndexGroupId)
      }

      if (!knowledgeBaseId) return null

      return {
        name: ROUTE_NAMES.knowledgeBases,
        params: { knowledgeBaseId }
      }
    },

    standaloneIndexes() {
      return this.indexes.filter(index => !index.index_group_id)
    },

    isEmailBot() {
      return botModule.isEmailBot()
    },

    botHasMarket() {
      return !!botModule.getBot().market_id
    },

    multiBotMarkets() {
      return getMarketsForCurrentBot()
    },

    isIndexTypeGroup() {
      return this.action?.indexType === INDEX_TYPES.GROUP
    },

    knowledgeBaseOptions() {
      const options = []
      const includedGroupIds = new Set()
      const shouldIncludeIndexGroups = this.botHasMarket || this.hideSections || this.isDisabled

      if (shouldIncludeIndexGroups) {
        this.indexGroups.forEach(group => {
          includedGroupIds.add(group.id)
          options.push({
            selectionKey: `${INDEX_TYPES.GROUP}:${group.id}`,
            id: group.id,
            name: group.name,
            indexType: INDEX_TYPES.GROUP
          })
        })

        this.indexes.forEach(index => {
          const groupId = index.index_group_id
          if (!groupId || includedGroupIds.has(groupId)) return

          includedGroupIds.add(groupId)
          const group = this.indexGroups.find(item => item.id === groupId)
          options.push({
            selectionKey: `${INDEX_TYPES.GROUP}:${groupId}`,
            id: groupId,
            name: group?.name || index.name,
            indexType: INDEX_TYPES.GROUP
          })
        })
      }

      this.standaloneIndexes.forEach(index => {
        options.push({
          selectionKey: `${INDEX_TYPES.SINGLE}:${index.id}`,
          id: index.id,
          name: index.name,
          indexType: INDEX_TYPES.SINGLE
        })
      })

      return options.sort((a, b) => a.name.localeCompare(b.name))
    },

    isKnowledgeBaseReady() {
      if (!this.actionIndexId) return true

      if (this.resolvedIndexGroupId) {
        const groupIndexes = this.indexes.filter(index => index.index_group_id === this.resolvedIndexGroupId)
        if (!groupIndexes.length) return true

        return groupIndexes.every(index => 'provider_status' in index)
      }

      const index = this.indexes.find(index => index.id === this.actionIndexId)
      return index && 'provider_status' in index
    },

    showKnowledgeBaseNotReadyWarning() {
      return !this.isKnowledgeBaseReady
    },

    selectedIndexGroupMissingMarkets() {
      if (!this.resolvedIndexGroupId) return null

      const missingMarkets = getMissingKnowledgeMarketsForIndexGroup(this.indexes, this.resolvedIndexGroupId, this.multiBotMarkets)
      if (!missingMarkets.length) return null

      const firstIndexId = getFirstIndexIdForIndexGroup(this.indexes, this.resolvedIndexGroupId)
      return {
        tooltip: buildMissingKnowledgeMarketsTooltip(missingMarkets),
        route: firstIndexId ?
          {
            name: ROUTE_NAMES.knowledgeBases,
            params: { knowledgeBaseId: firstIndexId }
          } :
          null
      }
    },

    knowledgeBaseSelectVariant() {
      if (this.isDisabled) return 'baseSelectDisabled'

      return this.isIndexIdError ? 'baseSelectError' : 'baseSelect'
    },

    isIndexIdError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Please select a knowledge base.'
    },

    isDeploymentIdError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'GPT Version value is not valid!'
    },

    // TODO Workaround to show the error. Real fix in https://github.com/amio-io/amio-chatbot-engine/issues/1286
    isPromptError() {
      return chatBubbleModule.isErrorBubbleFocused() && (this.errorDescription === 'Chat GPT prompt is not allowed to be empty!' || this.error.path === 'prompt')
    },

    isUserPromptError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.error.path === 'userPrompt'
    },

    isSearchMetadataError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.error.path === 'searchMetadata'
    },

    isMaxTokensError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Max tokens value is not valid!'
    },

    isTemperatureError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Temperature value is not valid!'
    },

    isTopPError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Top P value is not valid!'
    },

    isMessageHistoryCountError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Message History Count value is not valid!'
    },

    isTopNDocumentsError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Top N Documents value is not valid!'
    },

    isStrictnessError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Strictness value is not valid!'
    },

    isFrequencyPenaltyError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Frequency Penalty value is not valid!'
    },

    isPresencePenaltyError() {
      return chatBubbleModule.isErrorBubbleFocused() && this.errorDescription === 'Presence Penalty value is not valid!'
    },

    actionId() {
      return this.bubbleSelected.actionId
    },

    action() {
      return this.cachedAction || conversationModule.getAction(this.bubbleSelected.stateId, this.actionId)
    },

    previewController() {
      return templateModule.isTemplateEditor() ? templateController : answerController
    },

    isActionTypeChatGptCustom() {
      return this.action.type === ACTION_TYPES.CHAT_GPT_CUSTOM
    },

    includeMessageHistory() {
      return !!this.action.messageHistoryCount
    },

    // PRO-672: the request_input tool — offered to the model only when this is on
    allowInputFields() {
      return !!this.action.allowInputFields
    },

    gptVersionOptions() {
      const options = dropdownButtonsUtils.getGptVersionOptions()
      if (this.showUnreleased) return options

      return options.filter(option => {
        if (this.resolvedDeploymentId === option.value) return true
        if ([
          GPT_VERSIONS.FIVE_POINT_SIX_TERRA,
          GPT_VERSIONS.FIVE_POINT_SIX_SOL,
          GPT_VERSIONS.SIX_SOL
        ].includes(option.value)) return false

        return option.value !== GPT_VERSIONS.FOUR_OMNI_EU &&
          (!option.value.startsWith('gpt-5') || option.value === GPT_VERSIONS.FIVE_POINT_SIX_LUNA) &&
          (!option.value.startsWith('gpt-6') || option.value === GPT_VERSIONS.SIX_LUNA)
      })
    },

    resolvedDeploymentId() {
      switch (this.action.deploymentId) {
        case GPT_VERSIONS.FOUR_OMNI:
        case GPT_VERSIONS.FOUR_OMNI_GLOBAL:
          return GPT_VERSIONS.FOUR_OMNI_EU
        case GPT_VERSIONS.FOUR_TURBO:
        case GPT_VERSIONS.FOUR:
        case GPT_VERSIONS.THREE_POINT_FIVE:
          return GPT_VERSIONS.FOUR_OMNI_MINI
        default:
          return this.action.deploymentId
      }
    },

    isGpt5Deployment() {
      return this.resolvedDeploymentId?.startsWith('gpt-5') || this.resolvedDeploymentId?.startsWith('gpt-6')
    },

    selectedActionType() {
      if (this.action.type === ACTION_TYPES.CHAT_GPT_CUSTOM && !this.action.customDataOnly) return 'Custom + General'

      return CHATGPT_ACTION_TYPES_LABEL_MAP[this.action.type]
    },

    actionTypeOptions() {
      return Object.values(CHATGPT_ACTION_TYPES_LABEL_MAP)
    },

    creativityLevelOptions() {
      return Object.keys(CREATIVITY_LABEL_TEMPERATURE_MAP)
    },

    selectedCreativityLevel() {
      return Object.keys(CREATIVITY_LABEL_TEMPERATURE_MAP)
        .find(key => CREATIVITY_LABEL_TEMPERATURE_MAP[key] === this.action.temperature)
    },

    promptTokenCount() {
      if (!this.action?.prompt.length) return 0

      return this.tokenizer?.encode(this.action.prompt).length || 0
    },

    userPromptTokenCount() {
      if (!this.action?.userPrompt?.length) return 0

      return this.tokenizer?.encode(this.action.userPrompt).length || 0
    },

    searchMetadataTokenCount() {
      if (!this.action?.searchMetadata?.length) return 0

      return this.tokenizer?.encode(this.action.searchMetadata).length || 0
    },

    totalTokenCount() {
      return this.promptTokenCount + this.userPromptTokenCount + this.searchMetadataTokenCount
    }
  },

  methods: {
    removeChatGPT() {
      const node = stateTreeUtils.findNode(this.stateTree, this.bubbleSelected)
      this.previewController.removeBubble(node, this.template?.id)
    },

    updateActionType(label) {
      if (this.isDisabled) return

      const actionType = LABEL_CHATGPT_ACTION_TYPES_MAP[label]
      const action = [ACTION_TYPES.CHAT_GPT_CUSTOM, CHAT_GPT_CUSTOM_GENERAL].includes(actionType) ?
        conversationModule.createChatGPTCustomAction(this.action, label === 'Custom') :
        conversationModule.createChatGPTGeneralAction(this.action)
      conversationModule.updateChatGPTAction(this.actionId, action)
    },

    updateKnowledgeBaseSelection(selectionKey) {
      if (this.isDisabled) return

      const option = this.knowledgeBaseOptions.find(item => item.selectionKey === selectionKey)
      if (!option) return

      conversationModule.updateChatGPTAction(this.actionId, {
        ...this.action,
        indexId: option.id,
        indexType: option.indexType
      })
      if (this.isIndexIdError) this.error = null
    },

    updateActionCreativityLevel(label) {
      if (this.isDisabled) return

      const temperature = CREATIVITY_LABEL_TEMPERATURE_MAP[label]
      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, temperature })
      this.toggleRefreshKey = Math.random()
    },

    handleMessageHistoryCheckbox(includeMessageHistory) {
      const messageHistoryCount = includeMessageHistory ? 30 : 0
      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, messageHistoryCount })
    },

    handleAllowInputFieldsCheckbox(allowInputFields) {
      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, allowInputFields })
    },

    updateGptVersion(index) {
      if (this.isDisabled) return

      const { value: deploymentId } = this.gptVersionOptions[index]
      const updatedAction = { ...this.action, deploymentId }
      conversationModule.updateChatGPTAction(this.actionId, updatedAction)
      this.toggleRefreshKey = Math.random()
      this.resetError('deploymentId', deploymentId)
    },

    updateAction({ target }) {
      if (this.isDisabled) return
      if (['includeMessageHistoryCheckbox', 'allowInputFieldsCheckbox'].includes(target.id)) return

      const valueToSave = this.resolveValueToSave(target)
      conversationModule.updateChatGPTAction(this.actionId, { ...this.action, [target.name]: valueToSave })
      this.resetError(target.name, valueToSave)
    },

    resetError(name, value) {
      const isDeploymentIdFixed = this.isDeploymentIdError && name === 'deploymentId' && this.gptVersionOptions.some(option => option.value === value)
      const isPromptFixed = this.isPromptError && name === this.PROMPT.id && value.length > 0
      const isUserPromptFixed = this.isUserPromptError && name === this.USER_PROMPT.id && value.length > 0
      const isSearchMetadataFixed = this.isSearchMetadataError && name === this.SEARCH_METADATA.id && value.length > 0
      const isMaxTokensFixed = this.isMaxTokensError && name === this.MAX_TOKENS.id && value > 0 && value <= this.MAX_TOKENS.max && Number.isInteger(value)
      const isTemperatureFixed = this.isTemperatureError && name === this.TEMPERATURE.id && value >= 0 && value <= 2
      const isTopPFixed = this.isTopPError && name === this.TOP_P.id && value >= 0 && value <= 1
      const isTopNDocumentsFixed = this.isTopNDocumentsError && name === this.TOP_N_DOCUMENTS.id && value >= 1 && Number.isInteger(value)
      const isStrictnessFixed = this.isStrictnessError && name === this.STRICTNESS.id && value >= 1 && value <= 5 && Number.isInteger(value)
      const isFrequencyPenaltyFixed = this.isFrequencyPenaltyError && name === this.FREQUENCY_PENALTY.id && value >= -2 && value <= 2
      const isPresencePenaltyFixed = this.isPresencePenaltyError && name === this.PRESENCE_PENALTY.id && value >= -2 && value <= 2
      if (isDeploymentIdFixed || isPromptFixed || isUserPromptFixed || isSearchMetadataFixed || isMaxTokensFixed || isTemperatureFixed || isTopPFixed || isTopNDocumentsFixed || isStrictnessFixed || isFrequencyPenaltyFixed || isPresencePenaltyFixed) {
        this.error = null
      }
    },

    resolveValueToSave(target) {
      const { name, value, min, max } = target
      if ([this.PROMPT.id, this.USER_PROMPT.id, this.SEARCH_METADATA.id].includes(name)) return value

      return Number(this.resolveRange(value, min, max))
    },

    resolveRange(value, min, max) {
      if (+value > max) return max
      if (+value < min) return min

      return +value
    },

    async initTokenizer() {
      if (!window.GPTTokenizer_cl100k_base) {
        await new Promise(resolve => $script('https://unpkg.com/gpt-tokenizer', resolve))
      }
      this.tokenizer = window.GPTTokenizer_cl100k_base
    }
  },

  async mounted() {
    this.initTokenizer()
    this.isLoading = true
    try {
      const requests = [
        indexesService.list(),
        indexGroupService.list().catch(() => [])
      ]
      const indexGroupsIdx = 1

      let marketsIdx = null
      if (this.botHasMarket) {
        marketsIdx = requests.length
        requests.push(marketService.list().catch(() => []))
      }

      const results = await Promise.all(requests)

      this.indexes = results[0].sort((a, b) => a.name.localeCompare(b.name))
      this.indexGroups = results[indexGroupsIdx].sort((a, b) => a.name.localeCompare(b.name))
      if (marketsIdx !== null) {
        marketModule.setMarketList(results[marketsIdx] || [])
      }
    } finally {
      this.isLoading = false
    }
  }
}
</script>
